import React from 'react';
import PropTypes from 'prop-types';
import { Tooltip } from '@material-ui/core';
import { jobsStatuses } from '../../config/constants';
import { renderDateTime } from '../../helpers/dateTimeHelper';
import ExportProgressMeter from './ExportProgressMeter';
import './style.css';

const STATUS_CONFIG = {
  [jobsStatuses.STARTING]: {
    label: 'Starting',
    badgeClass: 'export-status-badge--starting',
    showProgress: true,
  },
  [jobsStatuses.PROCESSING]: {
    label: 'Processing',
    badgeClass: 'export-status-badge--processing',
    showProgress: true,
  },
  [jobsStatuses.FINISHING]: {
    label: 'Finishing',
    badgeClass: 'export-status-badge--finishing',
    showProgress: true,
  },
  [jobsStatuses.FINISHED]: {
    label: 'Finished',
    badgeClass: 'export-status-badge--finished',
    showProgress: false,
  },
  [jobsStatuses.ERROR]: {
    label: 'Error',
    badgeClass: 'export-status-badge--error',
    showProgress: false,
  },
};

const ExportJobBand = ({
  job,
  onRerunClick,
  onCloneClick,
  onFavoriteClick,
  onRemoveClick,
  t,
}) => {
  const isFinished = job.status === jobsStatuses.FINISHED;
  const isError = job.status === jobsStatuses.ERROR;
  const statusConfig = STATUS_CONFIG[job.status] || STATUS_CONFIG[jobsStatuses.PROCESSING];

  return (
    <tr className="export-table-row">
      <td className="export-table-cell export-table-cell--name">
        {job.name}
      </td>
      <td className="export-table-cell export-table-cell--date">
        {job.runStarted ? renderDateTime(job.runStarted) : '-'}
      </td>
      <td className="export-table-cell export-table-cell--status">
        {statusConfig.showProgress ? (
          <div className="export-status-progress">
            <span className={`export-status-badge ${statusConfig.badgeClass}`}>
              {statusConfig.label}
            </span>
            <ExportProgressMeter
              progress={job.progress || 0}
              status="active"
            />
          </div>
        ) : (
          <span className={`export-status-badge ${statusConfig.badgeClass}`}>
            {statusConfig.label}
          </span>
        )}
      </td>
      <td className="export-table-cell export-table-cell--actions">
        <div className="export-actions">
          <a
            href={job.downloadUrl}
            className={isFinished ? 'export-action-link' : 'export-action-link export-action-link--disabled'}
            onClick={(e) => !isFinished && e.preventDefault()}
          >
            <button
              type="button"
              className="export-btn export-btn--primary"
              disabled={!isFinished}
            >
              {t('export:downloadCSV')}
            </button>
          </a>

          <Tooltip placement="top" title={t('tooltips:rerunExportJob')}>
            <button
              type="button"
              className="export-btn export-btn--icon"
              onClick={() => onRerunClick(job)}
            >
              <span className="material-symbols-outlined">replay</span>
            </button>
          </Tooltip>

          <Tooltip placement="top" title={t('tooltips:cloneToNewExportJob')}>
            <button
              type="button"
              className="export-btn export-btn--icon"
              onClick={() => onCloneClick(job)}
            >
              <span className="material-symbols-outlined">content_copy</span>
            </button>
          </Tooltip>

          <Tooltip placement="top" title={t('tooltips:favoriteThisJob')}>
            <button
              type="button"
              className={`export-btn export-btn--icon ${job.isFavorite ? 'export-btn--favorite' : ''}`}
              onClick={() => onFavoriteClick(job)}
            >
              <span className="material-symbols-outlined">
                {job.isFavorite ? 'star' : 'star_outline'}
              </span>
            </button>
          </Tooltip>

          <Tooltip placement="top" title={t('tooltips:archiveThisJob')}>
            <button
              type="button"
              className="export-btn export-btn--icon export-btn--danger"
              disabled={!isFinished && !isError}
              onClick={() => onRemoveClick(job)}
            >
              <span className="material-symbols-outlined">delete_outline</span>
            </button>
          </Tooltip>
        </div>
      </td>
    </tr>
  );
};

ExportJobBand.propTypes = {
  job: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    progress: PropTypes.number,
    runStarted: PropTypes.string,
    isFavorite: PropTypes.bool,
    downloadUrl: PropTypes.string,
  }).isRequired,
  onRerunClick: PropTypes.func.isRequired,
  onCloneClick: PropTypes.func.isRequired,
  onFavoriteClick: PropTypes.func.isRequired,
  onRemoveClick: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default ExportJobBand;
